import {FC} from 'react'
import {FlyerData, listingImageSrc} from '../services/api'

const ACCENT = '#0c9444' // bright brand green
const DEEP = '#256b41' // forest green panels (boarding pass, transit, footer)
const INK = '#1b1b29'
const MUTED = '#7e8299'

/** Fixed poster width — keeps PNG/PDF exports crisp and predictable. */
export const FLYER_WIDTH = 820

// ─── Tiny inline icons ────────────────────────────────────────────────
const BedIcon: FC<{size?: number; color?: string}> = ({size = 20, color = '#fff'}) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M2 17v-5a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v4' />
    <path d='M2 17h20M2 12V7' />
    <circle cx='7' cy='10' r='1.4' />
  </svg>
)
/** Distinct bed icon per type (twin = single, full = double, queen = four-poster). */
const BedTypeIcon: FC<{type: string; size?: number}> = ({type, size = 26}) => {
  const p = {width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const}
  if (type === 'QUEEN')
    return (
      <svg {...p}>
        <path d='M4 20V5M20 20V5M4 6h16' />
        <path d='M5 16v-3h14v3M5 16h14' />
        <circle cx='8' cy='11.5' r='1.1' />
        <circle cx='16' cy='11.5' r='1.1' />
      </svg>
    )
  if (type === 'FULL')
    return (
      <svg {...p}>
        <path d='M2 18v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5' />
        <path d='M2 18h20M2 13V8' />
        <circle cx='6.5' cy='11' r='1.1' />
        <circle cx='17.5' cy='11' r='1.1' />
      </svg>
    )
  // TWIN — single
  return (
    <svg {...p}>
      <path d='M4 18v-4a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v4' />
      <path d='M4 18h11M4 14V9' />
      <circle cx='8' cy='11.5' r='1.1' />
    </svg>
  )
}
const Check: FC<{color?: string}> = ({color = ACCENT}) => (
  <svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' style={{flexShrink: 0, marginTop: 1}}>
    <circle cx='12' cy='12' r='10' />
    <path d='M8 12l2.5 2.5L16 9' />
  </svg>
)
const Plane: FC<{size?: number; color?: string; style?: React.CSSProperties}> = ({size = 40, color = ACCENT, style}) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill={color} style={style}>
    <path d='M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L11 19v-5.5z' />
  </svg>
)

const Dashed: FC = () => (
  <div style={{borderBottom: `2px dashed ${ACCENT}`, opacity: 0.5, margin: '8px 0 18px'}} />
)

/** Round "Pick up your keys today!" promo badge — green circle + key icon with
 *  curved text along the bottom. Set `image` to use a real key-fob photo. */
const KeysBadge: FC<{image?: string | null}> = ({image}) => (
  <div style={{position: 'absolute', top: 50, right: -10, width: 168, height: 156, pointerEvents: 'none', zIndex: 3}}>
    {/* Inner circle (photo or branded key graphic) */}
    <div style={{position: 'absolute', top: 6, left: 28, width: 112, height: 112, borderRadius: '50%', overflow: 'hidden', border: '3px solid #fff', boxShadow: '0 6px 16px rgba(27,27,41,0.28)', background: `linear-gradient(135deg, ${DEEP}, ${ACCENT})`, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      {image ? (
        <img src={image} crossOrigin='anonymous' alt='' style={{width: '100%', height: '100%', objectFit: 'cover'}} />
      ) : (
        <svg width='56' height='56' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
          <circle cx='8.5' cy='8.5' r='4.5' />
          <path d='M11.7 11.7 L20 20' />
          <path d='M17.5 17.5 l1.8 -1.8 M15.3 15.3 l1.8 -1.8' />
        </svg>
      )}
    </div>
    {/* Curved text hugging the circle's bottom edge (full line fits + reads) */}
    <svg viewBox='0 0 168 156' width={168} height={156} style={{position: 'absolute', inset: 0}}>
      <defs>
        <path id='ccp-keys-arc' d='M 14 62 A 70 70 0 0 0 154 62' fill='none' />
      </defs>
      <text fill={DEEP} fontSize='13' fontWeight='800' fontStyle='italic' letterSpacing='0.1'>
        <textPath href='#ccp-keys-arc' startOffset='50%' textAnchor='middle'>Pick up your keys today!</textPath>
      </text>
    </svg>
  </div>
)

const ListRow: FC<{children: React.ReactNode; padRight?: boolean; small?: boolean}> = ({children, padRight, small}) => (
  <div style={{display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: small ? 9.5 : 15, color: INK, marginBottom: small ? 7 : 11, lineHeight: 1.3, paddingRight: padRight ? 156 : 0}}>
    <Check />
    <span>{children}</span>
  </div>
)

/**
 * Promotional availability flyer matching the Crosscheck Crashpad brand layout:
 * hero with live bed count, monthly bed options + boarding-pass deposit ticket,
 * "why you'll love it" checklist with transit box, photo strip, and footer.
 * Rendered at a fixed width (id `flyer-poster`) for PNG/PDF/print export.
 */
export const FlyerPoster: FC<{data: FlyerData; id?: string}> = ({data, id = 'flyer-poster'}) => {
  const c = data.settings.content
  const hero = c.heroImage ? listingImageSrc(c.heroImage) : null
  const photos = c.photos.map(listingImageSrc).filter(Boolean)

  return (
    <div
      id={id}
      style={{
        width: FLYER_WIDTH,
        flexShrink: 0,
        background: '#fff',
        overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(27,27,41,0.16)',
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        color: INK,
      }}
    >
      {/* ── Hero ── */}
      <div style={{position: 'relative', height: 500, overflow: 'hidden', background: 'linear-gradient(120deg, #2b3a44, #4a5b66)'}}>
        {hero && <img src={hero} crossOrigin='anonymous' alt='' style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'}} />}

        {/* Black text panel — full height, solid on the left, diagonally fading into the photo */}
        <div style={{position: 'absolute', top: 0, left: 0, bottom: 0, width: '84%', background: 'linear-gradient(104deg, rgb(10, 13, 15) 0%, rgb(10, 13, 15) 0%, rgba(10, 13, 15, 0.72) 30%, rgba(10, 13, 15, 0.32) 66%, rgba(10, 13, 15, 0) 78%)'}} />

        {/* Logo (top-left) — solid white behind the logo, clean fade straight to transparent (no haze) */}
        <div style={{position: 'absolute', top: 0, left: 0, background: 'linear-gradient(108deg, #fff 0%, #fff 62%, rgba(255,255,255,0) 86%)', padding: '12px 60px 16px 24px', display: 'flex', alignItems: 'center', gap: 11}}>
          <img src='/app-icon.png' alt='' style={{width: 46, height: 46, borderRadius: 10, objectFit: 'cover'}} />
          <div style={{lineHeight: 1.0}}>
            <div style={{fontSize: 23, fontWeight: 800, color: ACCENT}}>Crosscheck</div>
            <div style={{fontSize: 23, fontWeight: 800, color: ACCENT}}>Crashpad</div>
          </div>
        </div>

        {/* Hero text over the black panel */}
        <div style={{position: 'absolute', top: 104, left: 30, color: '#fff'}}>
          <h1 style={{margin: 0, fontWeight: 800, lineHeight: 1.02, letterSpacing: '-0.01em', color: '#fff'}}>
            <span style={{display: 'block', fontSize: 42, color: '#fff'}}>{c.airportCode}</span>
            <span style={{display: 'block', fontSize: 42, color: '#fff'}}>Flight Crew</span>
            <span style={{display: 'block', fontSize: 42, color: ACCENT}}>CRASHPAD</span>
          </h1>
          <div style={{fontSize: 20, fontWeight: 600, letterSpacing: '0.3em', margin: '7px 0 16px', textTransform: 'uppercase'}}>{c.city}</div>

          {/* Beds-available pill — green outline + green text */}
          <div style={{display: 'inline-flex', alignItems: 'center', gap: 12, background: '#fff', border: `3px solid ${DEEP}`, borderRadius: 999, padding: '6px 24px 6px 6px'}}>
            <span style={{width: 48, height: 48, borderRadius: '50%', background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <BedIcon size={26} />
            </span>
            <span style={{fontSize: 38, fontWeight: 800, color: ACCENT, lineHeight: 1}}>{data.totals.availableBeds}</span>
            <span style={{fontSize: 16, fontWeight: 800, color: ACCENT, lineHeight: 1.05, letterSpacing: '0.04em'}}>BEDS<br />AVAILABLE</span>
          </div>

          {/* Badges */}
          <div style={{display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, fontSize: 17, fontWeight: 600}}>
            {c.badges.map((b, i) => (
              <span key={b} style={{display: 'flex', alignItems: 'center', gap: 10}}>
                {i > 0 && <span style={{width: 7, height: 7, borderRadius: '50%', background: ACCENT}} />}
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Curved bottom — gentle concave sweep rising from the lower-left up off the right edge */}
        <svg viewBox='0 0 820 200' preserveAspectRatio='none' style={{position: 'absolute', bottom: -1, left: 0, width: '100%', height: 200, display: 'block'}}>
          <path d='M0,200 L0,128 C330,132 580,116 824,16 L824,200 Z' fill='#fff' />
          <path d='M0,128 C330,132 580,116 824,16' fill='none' stroke={DEEP} strokeWidth='9' vectorEffect='non-scaling-stroke' />
        </svg>

      </div>

      {/* ── Body ── */}
      <div style={{display: 'flex', gap: 34, padding: '26px 34px 14px'}}>
        {/* Left column */}
        <div style={{flex: '1 1 50%', minWidth: 0}}>
          <h2 style={{fontSize: 24, fontWeight: 800, color: ACCENT, margin: 0, letterSpacing: '0.01em'}}>MONTHLY BED OPTIONS</h2>
          <Dashed />

          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            {c.bedOptions.map((o) => {
              const count = data.byType[o.type] ?? 0
              return (
                <div key={o.type} style={{display: 'flex', alignItems: 'stretch', borderRadius: 12, overflow: 'hidden', border: '1px solid #e7eaef', boxShadow: '0 4px 12px rgba(27,27,41,0.05)'}}>
                  <div style={{width: 58, background: DEEP, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                    <BedTypeIcon type={o.type} size={26} />
                  </div>
                  <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '14px 16px'}}>
                    <span style={{fontSize: 16, fontWeight: 600, color: INK}}>{count} {o.label}</span>
                    <span style={{fontSize: 15, color: MUTED, whiteSpace: 'nowrap'}}>
                      <span style={{display: 'inline-block', marginRight: 6, color: MUTED}}>—</span>
                      <b style={{fontSize: 24, fontWeight: 800, color: ACCENT}}>{o.price}</b> {c.priceSuffix}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Boarding-pass deposit ticket */}
          <div style={{marginTop: 16, background: DEEP, borderRadius: 12, overflow: 'hidden', color: '#fff'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>
              <span style={{display: 'flex', alignItems: 'center', gap: 7}}><Plane size={15} color='#fff' style={{transform: 'rotate(90deg)'}} /> BOARDING PASS</span>
              <span>{c.airportCode} → HOME BASE</span>
            </div>
            <div style={{background: '#fff', color: INK, margin: 0, display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px'}}>
              {/* barcode */}
              <div style={{width: 34, height: 78, flexShrink: 0, background: 'repeating-linear-gradient(90deg, #1b1b29 0, #1b1b29 2px, #fff 2px, #fff 5px)'}} />
              <div style={{flex: 1, textAlign: 'center'}}>
                <div style={{fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: INK}}>SECURE YOUR SPOT</div>
                <div style={{fontSize: 46, fontWeight: 800, color: ACCENT, lineHeight: 1.05}}>{c.depositAmount}</div>
                <div style={{fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: INK}}>DEPOSIT TO REGISTER</div>
              </div>
              <div style={{borderLeft: '2px dashed #cfd5de', paddingLeft: 14, flexShrink: 0}}>
                <Plane size={42} style={{transform: 'rotate(90deg)'}} />
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{flex: '1 1 50%', minWidth: 0, position: 'relative'}}>
          <KeysBadge image={c.keysImage ? listingImageSrc(c.keysImage) : null} />
          <h2 style={{fontSize: 24, fontWeight: 800, color: ACCENT, margin: 0}}>WHY YOU'LL LOVE IT ?</h2>
          <Dashed />

          {/* Reserve room on the right of the rows beside the keys badge. */}
          {c.amenities.map((a, i) => <ListRow key={a} padRight={i < 5} small>{a}</ListRow>)}

          {/* Transit */}
          <div style={{display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10}}>
            <Check />
            <span style={{fontSize: 15, fontWeight: 800, color: ACCENT, textDecoration: 'underline'}}>{c.transitTitle}</span>
          </div>
          <div style={{background: DEEP, borderRadius: 10, padding: '12px 14px', margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 9}}>
            {c.transit.map((t) => (
              <div key={t} style={{display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 14, color: '#fff', lineHeight: 1.3}}>
                <Check color='#fff' />
                <span>{t}</span>
              </div>
            ))}
          </div>

          {c.amenitiesExtra.map((a) => <ListRow key={a}>{a}</ListRow>)}
        </div>
      </div>

      {/* ── Photo strip ── */}
      {photos.length > 0 && (
        <div style={{display: 'flex', gap: 10, padding: '6px 34px 22px'}}>
          {photos.slice(0, 4).map((src, i) => (
            <div key={i} style={{flex: 1, height: 150, borderRadius: 8, overflow: 'hidden', background: '#eef1f5'}}>
              <img src={src} crossOrigin='anonymous' alt='' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
            </div>
          ))}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{background: DEEP, color: '#fff', textAlign: 'center', padding: '16px 24px', fontSize: 15, fontWeight: 600, letterSpacing: '0.02em'}}>
        {c.footerText}
      </div>
    </div>
  )
}
